import { ChartBaseModel } from '@src/slide/domain/ui-model/element-modeles/base-modeles/chart-base-model';
import { ENABLE_CHART_RENDER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';

import { MutationSource } from '@tencent/slide-sdk-model';
import { SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

import { createSubChartModelByType } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/sub-chart-factory';
import { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import { ChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-property';
import { supportSubChartTypes } from '@src/slide/domain/ui-model/element-modeles/constant/chart';
import { observable } from 'mobx';
import { BaseSubChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/base-sub-chart';
import {
    SubChartConverterFactory,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/plot-area/sub-chart/sub-chart-converter-factory';
import merge from 'lodash/merge';
import set from 'lodash/set';
import has from 'lodash/has';
import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';
import isObject from 'lodash/isObject';

import { toJSON } from '@src/slide/infrastructure/module/independent/object-operation';
import cloneDeep from 'lodash/cloneDeep';
import { transaction } from '@src/slide/domain/slide-app';
import {
    changeArrAppendToSetOrModify,
} from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';
import { getChangedPart, getPathsFromObject } from '@src/slide/infrastructure/module/independent/object/object-path';
import type {
    IChartTitle,
    IChartPlotArea,
    IChartLegend,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { ISubChartData, IChartData, IChartDataModel, DeltaOf, ICTLegend, IObservableDataModel, DataModelChange, ICTTitle, ICTShapeProperties, ICTColorStyle, ICTChartSpace } from '@tencent/slide-sdk-model';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import { ChartElementType, IChartElementGroup, SelectedElement } from '../Interface/chart-v3/chart-interaction.interface';
import type {
    ITextEditor,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IChartShapeProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property.interface';
import { ISubChartProperty } from '../property/chart/sub-chart';
import { IGroupSelectElement, ISelectElement } from '@tencent/chart/lib/chart-ui/interface';

export default class ChartModel extends ChartBaseModel implements IChartModel {
    public static async convertDataSdkToUi(sdkData, uiData, rawSdkData, option): Promise<void> {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData);

        const convertPromises: Array<Promise<any> | IBaseModel> = [];
        const convertChartTask = ModelConverterBridge.convertSdkToUi({
            type: UIModelType.CHART,
            sdkData,
            uiData,
        });
        convertPromises.push(convertChartTask);

        this.convertSubChartDataV3(sdkData, uiData);

        await Promise.all(convertPromises);
    }

    public static convertDataUiToSdk(uiData, sdkData, rawUiData) {
        ModelConverterBridge.convertUiToSdk({ type: UIModelType.CHART, uiData, sdkData, rawUiData });

        if (has(uiData, 'plotArea.charts')) {
            const subChartsSdkData = uiData.plotArea.charts.map((uiSubChart, index) => {
                if (!uiSubChart) {
                    return uiSubChart;
                }

                const sdkData: any = {};
                const converter = SubChartConverterFactory.getSubChartConverterByType(uiSubChart.type);
                converter?.convertSubChartDataUiToSdk(uiSubChart, sdkData);
                // 没有id的时候是新创建，这个时候subChart.sdkData是没有id的，因为subChart不走addShapes
                // 所以这里跟导入的时候一样，分配一个id，从1开始
                if (!uiSubChart.id) {
                    sdkData.id = `${index + 1}`;
                }
                return sdkData;
            });

            set(sdkData, 'chartSpace.chart.plotArea.charts', subChartsSdkData);
        }
    }

    public static didConvertDataSdkToUi(sdkData, uiData, rawSdk, optionObject) {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdk, optionObject);

        // 在model完成convert后，进行初始化property。v2/v3都可以按这个时序进行初始化
        try {
            uiData.initProperty();
        } catch (err) {
            console.error('[chart initProperty error]', err);
            window.log.error('[chart initProperty error]', err);
        }
    }

    private static convertSubChartDataV3(sdkData, uiData) {
        const sdkCharts = sdkData.props.chartSpace.chart.plotArea.charts;
        if (sdkCharts !== undefined) {
            // subCharts全量替换
            uiData.plotArea.charts = [];
            sdkCharts.map((sdkSubChart, index) => {
                // xsd中type可能不存在，前端对于type不存在的情况理论上应该做一个兼容、
                // todo 这里先设置成柱形图保证不报错，后续修改这里变成空值，下同
                if (!sdkSubChart?.type) {
                    return {
                        type: SubChartType.BarChart,
                    };
                }
                const uiSubChart = uiData.plotArea.charts[index]
                    || this.initialSubChartModel(sdkSubChart.type, String(index));
                if (!uiSubChart) {
                    return {
                        type: SubChartType.BarChart,
                    };
                }

                BaseSubChart.convertDataSdkToUi(sdkSubChart as ISubChartData, uiSubChart, undefined, undefined);

                uiData.plotArea.charts[index] = uiSubChart;
            });
            // 通过subChart，将axes挂到PlotArea上。
            this.getAxesFromSubChart(uiData.plotArea);
        }
    }

    private static initialSubChartModel(type: SubChartType, id: string): ISubChartModel | undefined {
        return createSubChartModelByType(type, id);
    }

    private static getAxesFromSubChart(uiData: IChartPlotArea) {
        // 先删除老uiData上的数据，再根据新数据重新set
        delete uiData.categoryAxes;
        delete uiData.valueAxes;
        delete uiData.dateAxes;

        uiData.charts.forEach((chart) => {
            const { axes } = chart;
            axes?.forEach((axis) => {
                const { type } = axis;
                if (type === 'category') {
                    // uiToSdk是全量数据，这里categoryAxes每次都重新根据subChart赋值，下同
                    uiData.categoryAxes = uiData.categoryAxes || [];
                    if (!uiData.categoryAxes.find(chartAxis => chartAxis.axId === axis.axId)) {
                        uiData.categoryAxes.push(axis);
                    }
                } else if (type === 'value') {
                    uiData.valueAxes = uiData.valueAxes || [];
                    if (!uiData.valueAxes.find(chartAxis => chartAxis.axId === axis.axId)) {
                        uiData.valueAxes.push(axis);
                    }
                } else if (type === 'date') {
                    uiData.dateAxes = uiData.dateAxes || [];
                    if (!uiData.dateAxes.find(chartAxis => chartAxis.axId === axis.axId)) {
                        uiData.dateAxes.push(axis);
                    }
                }
            });
        });
    }

    // 图表元素uiModel数据信息
    public title?: IChartTitle;
    public plotArea: IChartPlotArea;
    public legend?: IChartLegend;
    public style?: number;

    // 图表元素模块实例统一挂载在chartProperties下面，与model下的title等元素数据区分开
    public chartProperty: ChartProperty;
    public fallbackImageSrc?: string;
    // 图表是否需要更新，用于renderer判断是否需要重新绘制source。
    public shouldChartUpdate = false;
    public undoRedo: boolean;

    // 图表异步渲染的状态，由UI层同步这个状态
    public syncRenderStatus: Promise<any>;

    @observable public selectedElement: SelectedElement | null = null;
    @observable public selectionStyles = {};
    @observable public isDraggingInner = false;

    // title的editor实例，这里需要加上去让全局一些逻辑进行判定，比如delete事件
    private titleEditor: ITextEditor | null;
    private context: ChartContext;
    // 控制syncRenderStatus的resolve和reject
    private syncRenderResolver;
    private syncRenderRejecter;

    public constructor() {
        super();
        this.context = new ChartContext(this);
        this.syncRenderStatus = new Promise((resolve, reject) => {
            this.syncRenderResolver = resolve;
            this.syncRenderRejecter = reject;
        });
    }

    public initProperty() {
        this.chartProperty = new ChartProperty(this.context);
        this.chartProperty.init();
    }

    public setEditorComponent(editorComponent: ITextEditor | null): void {
        this.titleEditor = editorComponent;
    }

    public getContext(): ChartContext {
        return this.context;
    }

    public setDraggingInnerState(state: boolean) {
        this.isDraggingInner = state;
    }

    /**
     * 是否渲染成图表
     */
    public get isActualRenderChart(): boolean {
        // 如果开启图表渲染，按是否支持图表，来判定渲染图表还是兜底图
        if (ENABLE_CHART_RENDER) {
            return this.isChartSupported;
        }

        // 在关闭图表渲染下，
        // 如果图表支持，但是没有fallbackImageSrc（web插入），还是走图表渲染
        // 其他情况走兜底图
        return this.isChartSupported && !this.fallbackImageSrc;
    }

    /**
     * 是否是支持的图表类型
     */
    public get isChartSupported(): boolean {
        const subCharts = this.plotArea?.charts;
        if (!subCharts || subCharts.length < 1) {
            return false;
        }

        return subCharts.some(subchart => supportSubChartTypes.includes(subchart.type));
    }

    public get editorInstance(): ITextEditor | null {
        return this.titleEditor;
    }

    public setChartHasUpdated(hasUpdate: boolean) {
        this.shouldChartUpdate = !hasUpdate;
    }

    public resolveChartRender() {
        this.syncRenderResolver?.();
    }

    public rejectChartRender() {
        this.syncRenderRejecter?.();
    }

    public isElementSelected(id: string): boolean {
        return this.selectedElement?.id === id;
    }

    public isElementSelectedTypeOf(type: ChartElementType): boolean {
        const { selectedElement } = this;
        if (!selectedElement) return false;

        const sample = this.isSelectGroup() ? (selectedElement as IChartElementGroup).group[0]
            : this.selectedElement;

        return sample?.type === type;
    }

    public removeAllSelectedElements() {
        this.selectedElement = null;
    }

    public removeIfSelected(el: string | SelectedElement) {
        if (
            (typeof el === 'string' && this.selectedElement?.id === el)
            || this.selectedElement?.id === (el as SelectedElement).id
        ) {
            this.selectedElement = null;
        }
    }

    public markSelected(el: SelectedElement) {
        this.selectedElement = el;
    }

    public getSelectedElementProperty(showMarker?: boolean): any {
        const { selectedElement } = this;
        if (!selectedElement) return null;
        const selectedElementType = selectedElement.type;
        return this.chartProperty.getSelectedElementProperty(selectedElementType, showMarker);
    }

    public getChartSpaceProperty(): IChartShapeProperty {
        return this.chartProperty.getChartSpaceProperty();
    }

    public getSelectedSubChartProperty(): ISubChartProperty | undefined {
        return this.chartProperty.getSelectedSubChartProperty();
    }
    // 格式化应用，被格式刷调用
    public updateParagraphList(paragraphList) {
        const chartTitleProperty = this.chartProperty.chartTitle;

        if (chartTitleProperty) {
            chartTitleProperty.updateProps({ text: { paragraphList } });
        }
    }

    public updateProps(props: any): void {
        if (!this.sdkData) return; // 解绑后为null
        window.log.info(`${Object.getPrototypeOf(this).constructor.name}updateProps`, toJSON(props));

        const sdkData = this.prepareSDKPropsData(props);

        transaction(() => {
            // 子表的更新需要转成setSubChartMutation
            this.updateSubChartProps(sdkData);
            // 更新除子表外的其他属性
            this.updateChartSpaceProps(sdkData);
        });
    }

    public updateAndRemoveProps(updateProps, removeProps?) {
        if (!this.sdkData) return; // 解绑后为null
        const sUpdateProps = this.prepareSDKPropsData(updateProps);
        const sRemoveProps = this.prepareSDKPropsData(removeProps);

        this.sdkV3UpdateAndRemoveProps(this.sdkData, sUpdateProps, sRemoveProps);
    }

    protected updateSubChartProps(sdkData) {
        if (has(sdkData, 'chartSpace.chart.plotArea.charts')) {
            const chartModel = this.context.getChartModel();
            sdkData.chartSpace.chart.plotArea.charts.map((subChartSdkData, index) => {
                this.mergePatchWithSubChartSdkData(index, subChartSdkData);
                (chartModel.sdkData as IChartDataModel)?.api?.updateSubChartProps?.(sdkData, index);
            });

            // 更新完后从sdkData的patch上移除子表的数据，调用updateProps更新其余数据
            delete sdkData.chartSpace.chart.plotArea.charts;
        }
    }

    protected updateChartSpaceProps(sdkData) {
        const chartModel = this.context.getChartModel();

        this.mergeChartTextPatch(sdkData);
        this.mergeChartSpacePatch(sdkData);
        this.mergeChartLegend(sdkData);
        // 中台默认传递array是append模式，上层纠正为set或者modify模式
        changeArrAppendToSetOrModify(sdkData);


        (chartModel.sdkData as IChartDataModel)?.api.updateProps?.(sdkData);
    }

    protected sdkV3UpdateAndRemoveProps(uiSdk, sUpdateProps, sRemoveProps): void {
        const toRemovePaths = getPathsFromObject(sRemoveProps);
        toRemovePaths.forEach((path) => {
            // removeProps中目前可能包含undefined，如：[, null]表示删除第二个，所以这里做一下判断
            if (!has(sUpdateProps, path) && get(sRemoveProps, path) === null) {
                set(sUpdateProps, path, null);
            }
        });
        // 包含无用的set，剔除未变的属性
        const changedProps = getChangedPart(uiSdk.store.data, sUpdateProps);
        if (!Object.keys(changedProps).length) {
            return;
        }

        this.updateChartSpaceProps(sUpdateProps);
    }

    protected prepareSDKPropsData(chartData): any {
        const sdkData: any = {};
        Object.getPrototypeOf(this)
            .constructor
            .convertDataUiToSdk(chartData, sdkData, this);
        return sdkData;
    }

    /**
     * 3.0数据变更之后
     * @param sdkData
     * @param change
     * @protected
     */
    protected async sdkOnDidChangeV3(sdkData: IObservableDataModel, change?: DataModelChange): Promise<void> {
        if (change) {
            // v3 需要通过chartModel来判定是否UndoRedo，v2是在subChart上判定的
            this.undoRedo = change?.source === MutationSource.UndoRedo;

            // TODO 母版样式变更Slide里还没实现3.0，后续同步下实现
            const hasMasterStyleChange = false;

            const { title, plotArea, legend } = (((change.diffs as any)?.data)
                || ((change.diffs as any)?.props) as IChartData)?.chartSpace?.chart || {};
            // 图表核心数据变更
            const hasChartDataChange = title !== undefined || plotArea !== undefined || legend !== undefined;

            const spPrDiff = ((change.diffs as any)?.data as IChartData)?.spPr;
            // 图表宽高变更
            const hasRectChange = spPrDiff?.xfrm?.ext?.cx !== undefined || spPrDiff?.xfrm?.ext?.cy !== undefined;

            // 只有在数据变更 || 母版变更 || 大小变更的时候，才需要更新图标source
            const shouldChartUpdate = hasChartDataChange || hasMasterStyleChange || hasRectChange;
            this.setChartHasUpdated(!shouldChartUpdate);
        }
        await super.sdkOnDidChangeV3(sdkData, change);
    }

    private mergePatchWithSubChartSdkData(index: number, sdkDataPatch) {
        const chartSdkData = this.context.getChartModel().sdkData as IChartDataModel;
        const subChartSdkData = cloneDeep((chartSdkData as any).store.subChartList?.[index]);
        merge(subChartSdkData, sdkDataPatch);

        return subChartSdkData;
    }

    // 由于图表的文本和其他文本不一样，走的增量更新、非全量更新。目前sdk3.0 默认是append模式，逐层改成modify比较麻烦
    // 这里和sdkData做merge，设置成set模式。子表无需处理，因为子表本身就是全量替换，已经做过merge
    private mergeChartTextPatch(sdkPatchData: DeltaOf<IChartData>) {
        if (sdkPatchData.chartSpace?.chart?.title !== undefined) {
            const chartSdkData = this.context.getChartModel().sdkData as IChartDataModel;
            const titleSdkData = cloneDeep(chartSdkData?.getOwnedData()?.chartSpace?.chart?.title || {});
            const sdkTitlePatch = sdkPatchData.chartSpace?.chart?.title;
            const sdkTitleTextParaPatch = sdkTitlePatch?.tx?.rich?.p;

            merge(titleSdkData, sdkTitlePatch);

            // 上面的merge过程中，paragraphList不是替换行为，需要修正为替换（paragraphList已经处理成了全量）
            if (sdkTitleTextParaPatch && sdkTitleTextParaPatch.length > 0) {
                set(titleSdkData, 'tx.rich.p', sdkTitleTextParaPatch);
            }

            sdkPatchData.chartSpace.chart.title = titleSdkData as ICTTitle;
        }
    }

    // 图例数据全量merge
    // 中台图表的main节点都是replace，都需要做merge
    private mergeChartLegend(sdkPatchData: DeltaOf<IChartData>) {
        if (sdkPatchData.chartSpace?.chart?.legend !== undefined) {
            const chartSdkData = this.context.getChartModel().sdkData as IChartDataModel;
            const legendSdkData = cloneDeep(chartSdkData?.getOwnedData()?.chartSpace?.chart?.legend || {});

            merge(legendSdkData, sdkPatchData.chartSpace?.chart?.legend);
            sdkPatchData.chartSpace.chart.legend = legendSdkData as ICTLegend;
        }
    }

    private mergeChartSpacePatch(sdkPatchData: DeltaOf<IChartData>) {
        const chartSdkData = this.context.getChartModel().sdkData as IChartDataModel;
        // 图表的填充、边框、effect，在这里做merge,填充的聚合需要单独做处理
        if (sdkPatchData.chartSpace?.spPr !== undefined) {
            const chartSpaceSdkdata = cloneDeep(chartSdkData?.getOwnedData()?.chartSpace);
            // 当chartSpace.spPr不存在时，直接在表层chartSpace上做merge
            if (!chartSpaceSdkdata?.spPr) {
                const sdkChartSpacePatch = sdkPatchData.chartSpace;
                merge(chartSpaceSdkdata, sdkChartSpacePatch);
                sdkPatchData.chartSpace = chartSpaceSdkdata as ICTChartSpace;
            } else {
                const sdkChartSpaceShapePatch = sdkPatchData.chartSpace?.spPr;
                mergeWith(chartSpaceSdkdata.spPr, sdkChartSpaceShapePatch, this.mergeChartSpaceData);
                sdkPatchData.chartSpace = chartSpaceSdkdata as ICTChartSpace;
            }
        }
    }

    private mergeChartSpaceData = (objValue, srcValue, key) => {
        if (key === 'egColorChoice') {
            return srcValue; // 如果key是'egColorChoice'，则返回来源对象的值，不进行合并
        }
        if (isObject(srcValue) && isObject(objValue)) {
            return mergeWith(objValue, srcValue, this.mergeChartSpaceData);
        }
        return undefined;
    };

    private isSelectGroup() {
        const { selectedElement } = this;

        return !!(selectedElement as IChartElementGroup)?.group;
    }
}
